import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { getConfig } from '../translations/getTranslationConfig';
import { translationKeys } from '../translations/translationKeys';
import type { Language, Translations } from '../translations/translationsTypes';
import logger from '../utils/logger.ts';
import { resolveQueryRegion } from '../utils/resolveQueryRegion.ts';
import { useMergedConfig } from './MergedConfigProvider';

type TranslationsContextValue = {
  translations: Translations;
  isLoading: boolean;
  error: Error | null;
  translate: (key: translationKeys) => string;
};

const TranslationsContext = createContext<TranslationsContextValue | undefined>(undefined);

export function TranslationsProvider({ children }: { children: ReactNode }) {
  const { lang: configuredLanguage, region: configuredRegion } = useMergedConfig();

  const { search } = useLocation();
  const params = new URLSearchParams(search);

  const languageParam = params.get('language')?.trim();
  const languageValues = params
    .getAll('l')
    .map((value) => value.trim())
    .filter(Boolean);

  const language = (
    languageParam ||
    languageValues[languageValues.length - 1] ||
    configuredLanguage ||
    'en'
  ).toLowerCase() as Language;

  const region = (resolveQueryRegion(params) || configuredRegion || 'gb').toLowerCase();

  const [translations, setTranslations] = useState<Translations>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchTranslations = async () => {
      setIsLoading(true);
      setError(null);
      setTranslations([]);

      try {
        const { translations: fetched } = await getConfig(language);

        if (!cancelled) {
          setTranslations(fetched);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchTranslations();

    return () => {
      cancelled = true;
    };
  }, [language, region]);

  const translate = (key: translationKeys): string => {
    const candidates = translations.filter(
      (translation) => translation.keyName === key && translation.language.toLowerCase() === language
    );

    const regionalMatch = candidates.find((translation) =>
      translation.dimensionNameValues?.some(
        (dimension) => dimension.name.toLowerCase() === 'region' && dimension.value.toLowerCase() === region
      )
    );

    const genericMatch = candidates.find((translation) => {
      const regionDimension = translation.dimensionNameValues?.find(
        (dimension) => dimension.name.toLowerCase() === 'region'
      );

      return !regionDimension || regionDimension.value.trim() === '*';
    });

    const match =
      regionalMatch ?? genericMatch ?? candidates[0] ?? translations.find((translation) => translation.keyName === key);

    if (match?.translation) {
      return match.translation;
    }

    if (isLoading || translations.length === 0) {
      return `!!${key}`;
    }

    logger.error(`Translation not found for key: ${key}, language: ${language}, region: ${region}`);

    return `!!${key}`;
  };

  return (
    <TranslationsContext.Provider
      value={{
        translations,
        isLoading,
        error,
        translate,
      }}
    >
      {children}
    </TranslationsContext.Provider>
  );
}

export function useTranslationsContext(): TranslationsContextValue {
  const context = useContext(TranslationsContext);

  if (!context) {
    throw new Error('useTranslationsContext must be used within a TranslationsProvider');
  }

  return context;
}
